import type { Snapshot } from '../shared/protocol';

export type AudioStatus = 'off' | 'ready' | 'loading' | 'playing' | 'paused' | 'muted' | 'complete' | 'blocked' | 'unavailable';
export interface AudioState {
  snapshot: Snapshot | null;
  online: boolean;
  enabled: boolean;
  muted: boolean;
  volume: number;
  now: number;
}
type Media = Pick<HTMLAudioElement, 'src' | 'currentTime' | 'duration' | 'readyState' | 'paused' | 'muted' | 'volume' | 'play' | 'pause' | 'load' | 'removeAttribute' | 'addEventListener' | 'removeEventListener'>;
interface Dependencies {
  fetch: typeof fetch;
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  clock: () => number;
}

export function audioPosition(snapshot: Snapshot, now: number) {
  const remaining = snapshot.paused ? snapshot.remainingMs : snapshot.deadline === null ? null : snapshot.deadline - now;
  if (remaining === null || snapshot.phaseDurationMs === null || remaining <= 0) return null;
  return Math.max(0, snapshot.phaseDurationMs - remaining) / 1000;
}

export class AudioPlayback {
  private state: AudioState | null = null;
  private receivedAt = 0;
  private key = '';
  private abort: AbortController | null = null;
  private source = '';
  private attempted = false;
  private failure: 'blocked' | 'unavailable' | null = null;
  private pendingPlay = false;
  private playVersion = 0;
  private status: AudioStatus = 'off';
  private disposed = false;

  constructor(private media: Media, private token: string, private onStatus: (status: AudioStatus) => void, private deps: Dependencies) {
    media.addEventListener('loadedmetadata', this.sync);
    media.addEventListener('canplay', this.sync);
    media.addEventListener('playing', this.sync);
    media.addEventListener('error', this.failed);
  }

  update(state: AudioState) {
    if (this.disposed) return;
    this.state = state;
    this.receivedAt = this.deps.clock();
    const s = state.snapshot;
    const cue = s && ['welcome', 'intro', 'reading', 'question', 'reveal', 'leaderboard', 'finale'].includes(s.phase) && !(s.phase === 'reveal' && s.reveal?.voided) ? s.audioCue : undefined;
    const key = cue ? `${s!.gameId}:${s!.question?.id}:${s!.phase}:${cue.url}` : '';
    if (key !== this.key) {
      this.clearSource();
      this.key = key;
      this.attempted = false;
      this.failure = null;
    }
    this.media.volume = state.volume;
    this.media.muted = state.muted || !state.enabled;
    this.sync();
    const phaseElapsed = s ? audioPosition(s, state.now) : null;
    const canPrepare = cue && phaseElapsed !== null && phaseElapsed * 1000 < cue.durationMs + ('startOffsetMs' in cue ? cue.startOffsetMs ?? 0 : 0);
    if (cue && state.enabled && !state.muted && state.volume > 0 && state.online && !s!.paused && !this.attempted && (canPrepare || this.position() !== null)) {
      this.attempted = true;
      const abort = new AbortController();
      this.abort = abort;
      this.setStatus('loading');
      void this.deps.fetch(cue.url, { headers: { Authorization: `Bearer ${this.token}` }, signal: abort.signal, cache: 'no-store' })
        .then(response => {
          if (!response.ok) throw new Error('Audio unavailable');
          return response.blob();
        })
        .then(blob => {
          if (this.disposed || abort.signal.aborted || this.key !== key) return;
          this.source = this.deps.createObjectURL(blob);
          this.media.src = this.source;
          this.media.load();
          this.sync();
        })
        .catch(() => {
          if (!this.disposed && !abort.signal.aborted && this.key === key) { this.failure = 'unavailable'; this.sync(); }
        });
    }
  }

  retry() {
    if (this.failure === 'blocked') this.failure = null;
    if (this.failure === 'unavailable') {
      this.failure = null; this.attempted = false;
      if (this.state) this.update(this.state);
    }
    this.sync();
  }

  dispose() {
    this.disposed = true;
    this.media.removeEventListener('loadedmetadata', this.sync);
    this.media.removeEventListener('canplay', this.sync);
    this.media.removeEventListener('playing', this.sync);
    this.media.removeEventListener('error', this.failed);
    this.clearSource();
  }

  private position() {
    const state = this.state;
    if (!state?.snapshot || !this.key) return null;
    const now = state.now + Math.max(0, this.deps.clock() - this.receivedAt);
    const cue = state.snapshot.audioCue;
    if (!cue) return null;
    const duration = this.source && Number.isFinite(this.media.duration) ? this.media.duration : cue.durationMs / 1000;
    const elapsed = audioPosition(state.snapshot, now);
    const position = elapsed === null ? null : elapsed - ('startOffsetMs' in cue ? cue.startOffsetMs ?? 0 : 0) / 1000;
    return position !== null && position >= 0 && position < duration ? position : null;
  }

  private setStatus(status: AudioStatus) {
    if (status !== this.status && !this.disposed) { this.status = status; this.onStatus(status); }
  }

  private stop() {
    this.playVersion++;
    this.pendingPlay = false;
    this.media.pause();
  }

  private clearSource() {
    this.abort?.abort();
    this.abort = null;
    this.stop();
    this.media.removeAttribute('src');
    this.media.load();
    if (this.source) this.deps.revokeObjectURL(this.source);
    this.source = '';
  }

  private failed = () => {
    if (!this.source || this.disposed) return;
    this.failure = 'unavailable';
    this.sync();
  };

  private sync = () => {
    const state = this.state;
    if (!state || this.disposed) return;
    const position = this.position();
    const stopped: AudioStatus | null = !state.enabled ? 'off' : state.muted || state.volume === 0 ? 'muted'
      : !state.online || state.snapshot?.paused ? 'paused' : !this.key ? 'ready' : position === null ? 'complete' : this.failure;
    if (stopped) { this.stop(); this.setStatus(stopped); return; }
    if (!this.source || this.media.readyState < 1) { this.setStatus('loading'); return; }
    try {
      if (this.media.paused || Math.abs(this.media.currentTime - position!) > 0.75) this.media.currentTime = position!;
      if (this.pendingPlay) return;
      if (!this.media.paused) { this.setStatus('playing'); return; }
      this.pendingPlay = true;
      const version = ++this.playVersion;
      void this.media.play().then(() => {
        if (this.disposed || version !== this.playVersion) return;
        this.pendingPlay = false;
        this.sync();
      }).catch((error: unknown) => {
        if (this.disposed || version !== this.playVersion) return;
        this.pendingPlay = false;
        this.failure = error instanceof Error && error.name === 'NotAllowedError' ? 'blocked' : 'unavailable';
        this.sync();
      });
    } catch { this.pendingPlay = false; this.failure = 'unavailable'; this.stop(); this.setStatus('unavailable'); }
  };
}
